import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowUpRight, Check, ChevronRight, Infinity as InfinityIcon, Lock, RefreshCw } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import CredibilityStrip from "./CredibilityStrip";
import PricingPlanCard from "./PricingPlanCard";
import PricingAddons from "./PricingAddons";
import SEO, { SITE_URL } from "../SEO";
import { Reveal, Overline } from "../Reveal";
import { Button } from "../ui/button";
import CurrencySelector from "../CurrencySelector";
import { FaqSection } from "../FaqList";
import FeatureArt from "../FeatureArt";
import { FEATURE_CATEGORIES, featurePath } from "../../data/features";
import {
  ALL_PLANS,
  PLAN_LEVEL_LABEL,
  PRICING_LAST_UPDATED,
  PRICING_PROMOTION,
  SIGNUP_URL,
  channelPrice,
  formatINR,
  getPlanBySlug,
  planDisplayName,
  planHighlights,
  planPath,
  planSections,
} from "../../data/content";
import { planFaqs } from "../../data/pageFaqs";
import { CurrencyProvider, useCurrency } from "../../contexts/CurrencyContext";
import { convertFromINR } from "../../data/currencies";

// Payment views per level - `key` matches a key in the plan's `pricing`.
// Lifetime (one-time) pricing is shown for the Unlimited plans only - it is
// deliberately left off the Channel Based plan pages.
const VIEWS = {
  channel: [{ key: "yearly", label: "First Year" }],
  unlimited: [
    { key: "monthly", label: "Monthly" },
    { key: "yearly", label: "Annual" },
    { key: "onetime", label: "Lifetime" },
  ],
};

// Every way to pay for the plan. Amounts are INR; `note` takes the page's
// money formatter so it follows the selected currency.
function paymentOptions(plan) {
  const { monthly, yearly, onetime } = plan.pricing;
  if (plan.level === "channel") {
    return [
      {
        option: "First Year",
        amount: yearly.amount,
        unit: "/ year",
        note: () => `Covers ${plan.channelAllowance.toLowerCase()} for the first year.`,
      },
    ];
  }
  return [
    {
      option: "Monthly",
      amount: monthly.amount,
      unit: "/ month",
      note: (money) => `Plus a one-time setup fee of ${money(monthly.setup)}.`,
    },
    { option: "Annual", amount: yearly.amount, unit: "/ year", note: () => "One payment per year, no setup fee." },
    { option: "Lifetime", amount: onetime.amount, unit: "one-time", note: () => "Pay once, own it." },
  ];
}

// Channel Based plans renew per active channel from the second year - the
// same per-channel rate the calculator on /pricing shows. INR.
const renewalPerChannel = (plan) =>
  plan.level === "channel" ? channelPrice(1, "yearly") : null;

function ViewTabs({ views, view, onChange }) {
  return (
    <div role="tablist" aria-label="Billing" className="inline-flex items-center gap-1 rounded-full border border-[#E6E9F1] bg-white p-1 shadow-[0_2px_10px_rgba(11,21,51,0.05)]">
      {views.map((v) => (
        <button
          key={v.key}
          role="tab"
          type="button"
          aria-selected={view === v.key}
          data-testid={`plan-view-tab-${v.key}`}
          onClick={() => onChange(v.key)}
          className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${
            view === v.key ? "bg-[#FF6B00] text-white shadow-[0_6px_16px_rgba(255,107,0,0.35)]" : "text-[#475569] hover:text-[#0B1533]"
          }`}
        >
          {v.label}
        </button>
      ))}
    </div>
  );
}

// Comparison-table rows that are a product with its own feature page -
// shown as artwork tiles, like the /features grid.
const ROW_FEATURE = {
  "Google Business Profile": "gmb",
  "Facebook Automation": "facebook",
  "Instagram Automation": "instagram",
  "WhatsApp API CRM": "whatsapp",
  "WordPress Automation": "wordpress",
  "AEO + GEO": "aeo-geo",
  Websites: "bio-links",
  "Instant Website": "bio-links",
  "MagicQR + Review & Feedback": "review-routing",
  "Digital Menu": "menu",
  "Digital Visiting Card": "qr-nfc",
  "Bio Links": "bio-links",
  "Appointment Scheduling": "appointments",
};
const featureFor = (label) => FEATURE_CATEGORIES.find((c) => c.id === ROW_FEATURE[label]);

// Every other row is a capability: its icon and description come from the
// feature point of the same name, where one exists.
const POINTS = new Map(FEATURE_CATEGORIES.flatMap((c) => c.points.map((p) => [p.title, p])));
const POINT_ALIAS = { "White-Label": "Full White-Label" };
const pointFor = (label) => POINTS.get(POINT_ALIAS[label] || label);

// This plan's column of the comparison matrix, one entry per row.
const planRows = (plan) =>
  planSections(plan).flatMap((section) =>
    section.rows.map((row) => {
      const value = row.values[plan.key];
      const unlimited = value === "Unlimited" || (value === true && Boolean(section.unlimited || row.unlimited));
      return {
        label: row.label,
        section: section.title,
        included: value !== false,
        unlimited,
        status: value === false ? "Not included" : unlimited ? "Unlimited" : value === true ? "Included" : value,
      };
    })
  );
const includedFirst = (rows) => [...rows.filter((r) => r.included), ...rows.filter((r) => !r.included)];

function StatusPill({ row }) {
  const tone = !row.included
    ? "bg-[#E6E9F1] text-[#64748B]"
    : row.unlimited
      ? "bg-[#FF6B00] text-white"
      : "bg-[#FF6B00]/10 text-[#FF6B00]";
  const Icon = !row.included ? Lock : row.unlimited ? InfinityIcon : Check;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold whitespace-nowrap ${tone}`}>
      <Icon className="h-3 w-3" strokeWidth={3} />
      {row.status}
    </span>
  );
}

// Product tile: the feature's artwork, linking to its feature page.
function ProductTile({ row }) {
  const cat = featureFor(row.label);
  return (
    <Link
      to={featurePath(cat)}
      data-testid="plan-product-tile"
      className={`group flex h-full flex-col items-center rounded-3xl border px-4 pb-5 pt-3 text-center transition-[transform,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00]/50 ${
        row.included
          ? "border-[#E6E9F1] bg-white mb-card-shadow hover:-translate-y-1 hover:shadow-[0_16px_36px_rgba(11,21,51,0.12)]"
          : "border-dashed border-[#CBD2E0] bg-[#F9FAFC]"
      }`}
    >
      <FeatureArt
        cat={cat}
        alt={`${row.label} - MagicBolt AI`}
        className={`max-w-[150px] ${row.included ? "" : "opacity-40 grayscale"}`}
      />
      <h3 className={`mt-2 text-sm font-bold leading-snug sm:text-base ${row.included ? "text-[#0B1533]" : "text-[#94A3B8]"}`}>
        {row.label}
      </h3>
      <div className="mt-auto pt-3">
        <StatusPill row={row} />
      </div>
    </Link>
  );
}

function CapabilityCard({ row }) {
  const point = pointFor(row.label);
  const Icon = point?.icon || Check;
  return (
    <li
      data-testid="plan-capability-card"
      className={`flex gap-4 rounded-2xl border p-5 ${
        row.included ? "border-[#E6E9F1] bg-[#F4F6FB]" : "border-dashed border-[#CBD2E0] bg-[#F9FAFC]"
      }`}
    >
      <span
        className={`grid h-11 w-11 shrink-0 place-items-center rounded-lg ${
          row.included ? "bg-[#FF6B00]/10 text-[#FF6B00]" : "bg-[#E6E9F1] text-[#94A3B8]"
        }`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className={`text-base font-semibold ${row.included ? "text-[#0B1533]" : "text-[#94A3B8]"}`}>{row.label}</h4>
          <StatusPill row={row} />
        </div>
        {point && row.included && <p className="mt-1.5 text-sm text-[#475569] leading-relaxed">{point.desc}</p>}
      </div>
    </li>
  );
}

function PlanDetailContent({ plan }) {
  const views = VIEWS[plan.level];
  const [view, setView] = useState(views[0].key);
  const { currency } = useCurrency();
  const money = (inr) => `${currency.symbol}${Math.round(convertFromINR(inr, currency)).toLocaleString("en-US")}`;

  const name = planDisplayName(plan.name);
  const path = planPath(plan);
  const level = PLAN_LEVEL_LABEL[plan.level];
  const highlights = planHighlights(plan);
  const options = paymentOptions(plan);
  const renewal = renewalPerChannel(plan);
  const hasLifetime = views.some((v) => v.key === "onetime");
  const faqs = planFaqs(plan);
  const siblings = ALL_PLANS.filter((p) => p.level === plan.level && p.key !== plan.key);
  const otherLevel = ALL_PLANS.filter((p) => p.level !== plan.level);
  const rows = planRows(plan);
  const productRows = includedFirst(rows.filter((r) => featureFor(r.label)));
  // "Total Channels" is the plan's allowance, already shown as its badge.
  const capabilityRows = rows.filter((r) => !featureFor(r.label) && r.label !== "Total Channels");
  const capabilityGroups = [...new Set(capabilityRows.map((r) => r.section))].map((title) => ({
    title,
    rows: includedFirst(capabilityRows.filter((r) => r.section === title)),
  }));
  const usesWithinLimit = rows.some((r) => r.status === "Within Limit");

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `MagicBolt AI - ${name} Plan`,
    description: plan.tagline,
    brand: { "@type": "Brand", name: "MagicBolt AI" },
    url: `${SITE_URL}${path}`,
    offers: [
      ["Yearly plan", plan.pricing.yearly.amount],
      ...(hasLifetime ? [["Lifetime (one-time cost)", plan.pricing.onetime.amount]] : []),
    ].map(([offerName, price]) => ({
      "@type": "Offer",
      name: offerName,
      price,
      priceCurrency: "INR",
      availability: "https://schema.org/InStock",
      url: `${SITE_URL}${path}`,
    })),
  };

  return (
    <div className="min-h-screen bg-[#F4F6FB]" data-testid="pricing-plan-detail-page">
      <SEO
        title={`${name} Plan - Pricing & Features | MagicBolt AI`}
        description={`${plan.tagline} From ${formatINR(plan.pricing.yearly.amount)}/year${hasLifetime ? ` or ${formatINR(plan.pricing.onetime.amount)} one-time` : ""}. See everything the ${name} plan includes.`}
        path={path}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Pricing", path: "/pricing" },
          { name: `${name} Plan`, path },
        ]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 pb-16 md:pb-20 overflow-hidden bg-white border-b border-[#E6E9F1]">
        <div className="mb-blob mb-blob-orange mb-anim-a w-[360px] h-[360px] -top-24 right-0" />
        <div className="absolute inset-0 mb-grid-lines opacity-60" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
          <nav aria-label="Breadcrumb" className="mb-8 flex flex-wrap items-center gap-1.5 text-sm text-[#94A3B8]">
            <Link to="/" className="hover:text-[#0B1533]">Home</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to="/pricing" className="hover:text-[#0B1533]">Pricing</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-[#475569]">{name} Plan</span>
          </nav>

          <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Overline>{level} Plan</Overline>
              <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.05] text-[#0B1533]">
                <span className="mb-gradient-text">{name} Plan</span> by MagicBolt AI
              </h1>
              <p className="mt-6 text-base md:text-lg text-[#475569] leading-relaxed">{plan.tagline}</p>
              <p className="mt-3 text-sm md:text-base text-[#64748B] leading-relaxed">
                {plan.positioning || plan.microcopy}
              </p>

              <ul className="mt-8 grid gap-3 sm:grid-cols-2" data-testid="plan-highlights">
                {highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2.5 text-sm font-semibold text-[#334155]">
                    <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#FF6B00]/10">
                      <Check className="h-3 w-3 text-[#FF6B00]" strokeWidth={3} />
                    </span>
                    {h}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="plan-detail-cta"
                  size="lg"
                  className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold rounded-lg h-12 px-7 text-base hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(255,107,0,0.35)]"
                  asChild
                >
                  <a href={SIGNUP_URL}>{plan.cta} <ArrowRight className="ml-2 w-4 h-4" /></a>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-[#CBD2E0] bg-white text-[#0B1533] hover:bg-white hover:text-[#0B1533] font-semibold rounded-lg h-12 px-7 text-base"
                  asChild
                >
                  <Link to={`/pricing?plan=${plan.level}#pricing-comparison`}>Compare All Plans</Link>
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mx-auto flex w-full max-w-md flex-col"
            >
              <div className="mb-5 flex flex-wrap items-center justify-center gap-3">
                {views.length > 1 && <ViewTabs views={views} view={view} onChange={setView} />}
                <CurrencySelector />
              </div>
              <PricingPlanCard plan={plan} view={view} />
              {renewal != null && (
                <div
                  data-testid="plan-renewal-price"
                  className="mt-4 flex items-center gap-3 rounded-2xl border border-dashed border-[#FF6B00]/50 bg-gradient-to-r from-[#FFF3E9] to-[#FFFBF6] px-4 py-3"
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#FF6B00] text-white shadow-[0_6px_14px_rgba(255,107,0,0.35)]">
                    <RefreshCw className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 text-left">
                    <div className="text-xs font-semibold uppercase tracking-wide text-[#B45300]">From Next Year</div>
                    <div className="text-sm font-bold text-[#0B1533]">
                      Renewal Price <span className="text-[#FF6B00]">{money(renewal)}</span> per Active channel per year
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Every way to pay */}
      <section id="plan-pricing" className="py-16 md:py-20 scroll-mt-24" data-testid="plan-payment-options">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="max-w-2xl">
                <h2 className="text-3xl md:text-4xl font-bold tracking-tighter leading-tight text-[#0B1533]">
                  How much does the {name} plan cost?
                </h2>
              </div>
              <CurrencySelector />
            </div>
            <div className={`mt-10 grid gap-5 ${options.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
              {options.map((o) => (
                <div key={o.option} className="rounded-2xl border border-[#E6E9F1] bg-white p-6 mb-card-shadow">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#FF6B00]">{o.option}</div>
                    <span
                      data-testid="plan-option-discount"
                      className="rounded-full bg-gradient-to-r from-[#FF6B00] to-[#FF8533] px-3 py-1 text-xs font-extrabold tracking-wide text-white shadow-[0_6px_16px_rgba(255,107,0,0.35)]"
                    >
                      {PRICING_PROMOTION.discountPercent}% OFF
                    </span>
                  </div>
                  <div className="mt-3 flex items-baseline gap-1.5">
                    <span className="text-3xl font-extrabold tracking-tight text-[#0B1533]">{money(o.amount)}</span>
                    <span className="text-xs font-semibold text-[#94A3B8]">{o.unit}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-[#94A3B8] line-through">
                      {money(o.amount * PRICING_PROMOTION.listPriceMultiplier)}
                    </span>
                    <span className="rounded-md bg-[#FFF4D6] px-2.5 py-1 text-xs font-bold text-[#8A5A00]">
                      You save {money(o.amount * (PRICING_PROMOTION.listPriceMultiplier - 1))}
                    </span>
                  </div>
                  {o.note && <p className="mt-3 text-sm text-[#475569] leading-relaxed">{o.note(money)}</p>}
                </div>
              ))}
              {renewal != null && (
                <div
                  data-testid="plan-option-renewal"
                  className="rounded-2xl border border-dashed border-[#FF6B00]/50 bg-gradient-to-br from-[#FFF3E9] to-white p-6"
                >
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-[#FF6B00]">
                    <RefreshCw className="h-3.5 w-3.5" /> From Next Year
                  </div>
                  <div className="mt-3 flex flex-wrap items-baseline gap-1.5">
                    <span className="text-3xl font-extrabold tracking-tight text-[#0B1533]">{money(renewal)}</span>
                    <span className="text-xs font-semibold text-[#94A3B8]">per Active channel / year</span>
                  </div>
                  <p className="mt-3 text-sm text-[#475569] leading-relaxed">
                    Renewal Price {money(renewal)} per Active channel per year, charged from the second year.
                  </p>
                </div>
              )}
            </div>
            <p className="mt-5 text-sm text-[#64748B]">
              Prices are set in INR and shown at a{" "}
              <strong className="font-bold text-[#FF6B00]">
                limited-time {PRICING_PROMOTION.discountPercent}% discount
              </strong>{" "}
              off the list price. Pricing last updated {PRICING_LAST_UPDATED}.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Full inclusion list - this plan's column of the comparison table */}
      <section className="py-16 md:py-20 bg-white border-y border-[#E6E9F1]" data-testid="plan-included">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="max-w-2xl">
                <h2 className="text-3xl md:text-4xl font-bold tracking-tighter leading-tight text-[#0B1533]">
                  What is included in the {name} plan?
                </h2>
              </div>
              <Link
                to={`/pricing?plan=${plan.level}#pricing-comparison`}
                className="inline-flex items-center gap-1 text-sm font-semibold text-[#FF6B00] hover:underline"
              >
                Compare with other plans <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-6" data-testid="plan-product-grid">
              {productRows.map((row) => (
                <ProductTile key={row.label} row={row} />
              ))}
            </div>
            {usesWithinLimit && (
              <p className="mt-5 text-sm text-[#64748B]">
                <span className="font-semibold text-[#0B1533]">Within Limit</span> - available, and each one you
                connect counts towards the plan's {plan.channelAllowance.toLowerCase()}.
              </p>
            )}

            {capabilityGroups.map((group) => (
              <div key={group.title} className="mt-14">
                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#94A3B8]">{group.title}</h3>
                <ul className="mt-5 grid gap-4 md:grid-cols-2">
                  {group.rows.map((row) => (
                    <CapabilityCard key={row.label} row={row} />
                  ))}
                </ul>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <PricingAddons />

      {/* Other plans */}
      <section className="py-16 md:py-20 bg-white border-t border-[#E6E9F1]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tighter text-[#0B1533]">
                Other {level} plans
              </h2>
              <Link to="/pricing" className="inline-flex items-center gap-1 text-sm font-semibold text-[#FF6B00] hover:underline">
                All pricing <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {siblings.map((p) => (
                <Link
                  key={p.key}
                  to={planPath(p)}
                  data-testid="related-plan"
                  className="group flex flex-col rounded-xl border border-[#E6E9F1] bg-[#F4F6FB] p-5 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(11,21,51,0.10)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00]/50"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-base font-bold text-[#0B1533]">{planDisplayName(p.name)}</span>
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-[#CBD2E0] transition-colors group-hover:text-[#FF6B00]" />
                  </div>
                  <p className="mt-1.5 flex-1 text-sm text-[#475569] leading-relaxed">{p.tagline}</p>
                  <div className="mt-3 text-sm font-extrabold text-[#FF6B00]">
                    {money(p.pricing.yearly.amount)} <span className="text-xs font-semibold text-[#94A3B8]">/ year</span>
                  </div>
                </Link>
              ))}
            </div>
            <p className="mt-6 text-sm text-[#475569]">
              Also see the {PLAN_LEVEL_LABEL[otherLevel[0].level]} plans:{" "}
              {otherLevel.map((p, i) => (
                <span key={p.key}>
                  {i > 0 && ", "}
                  <Link to={planPath(p)} className="font-semibold text-[#FF6B00] hover:underline">
                    {planDisplayName(p.name)}
                  </Link>
                </span>
              ))}
              .
            </p>
          </Reveal>
        </div>
      </section>

      <FaqSection title={`Frequently asked questions about the ${name} plan`} items={faqs} id="plan-faq" tinted>
        <p className="mt-6 text-sm">
          <Link to="/faq" className="font-semibold text-[#FF6B00] hover:underline">All FAQs</Link>
          <span className="text-[#94A3B8]"> · </span>
          <Link to="/refund-policy" className="font-semibold text-[#FF6B00] hover:underline">Refund Policy</Link>
        </p>
      </FaqSection>

      {/* CTA */}
      <section className="relative py-24 border-t border-[#E6E9F1] overflow-hidden bg-white">
        <div className="mb-blob mb-blob-orange mb-anim-a w-[360px] h-[360px] -top-20 left-1/2 -translate-x-1/2" />
        <div className="absolute inset-0 mb-radial-glow" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter leading-none text-[#0B1533]">
              Get started with the {name} plan
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#475569] max-w-xl mx-auto leading-relaxed">{plan.microcopy}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold rounded-lg h-12 px-7 text-base hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(255,107,0,0.35)]"
                asChild
              >
                <a href={SIGNUP_URL}>{plan.cta} <ArrowRight className="ml-2 w-4 h-4" /></a>
              </Button>
              <Button
                data-testid="plan-cta-view-plan"
                size="lg"
                variant="outline"
                className="border-[#FF6B00] bg-white text-[#FF6B00] hover:bg-[#FFF3E9] hover:text-[#FF6B00] font-semibold rounded-lg h-12 px-7 text-base"
                asChild
              >
                <Link to={`${path}#plan-pricing`}>View {name} Plan</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-[#CBD2E0] bg-white text-[#0B1533] hover:bg-white hover:text-[#0B1533] font-semibold rounded-lg h-12 px-7 text-base"
                asChild
              >
                <Link to="/pricing">Compare Plans</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <CredibilityStrip />

      <Footer showCta={false} />
    </div>
  );
}

export default function PricingPlanDetailPage() {
  const { slug } = useParams();
  const plan = getPlanBySlug(slug);
  if (!plan) return <Navigate to="/pricing" replace />;
  return (
    <CurrencyProvider>
      {/* key resets the billing tab when moving between plan pages */}
      <PlanDetailContent key={plan.key} plan={plan} />
    </CurrencyProvider>
  );
}
